import { CircleAlert, Check } from "lucide-react"
import { useToast as useShadToast } from "@f2e/ui/components/base-legacy/use-toast"
import type { ToastProps } from "@f2e/ui/components/base-legacy/use-toast"
import { ToastAction } from "@f2e/ui/components/base-legacy/toast"
import { ReactNode, useCallback } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "@f2e/ui/lib/utils"

import { validationErrorToastClassName } from "@/lib/apiErrors"
import { getSupportMailto } from "@/lib/support"

const DEFAULT_DURATION = 10000
type ToastPayload = Omit<ToastProps, "title" | "description"> & {
  title?: ReactNode
  description?: ReactNode
}

export interface AlertToastProps extends ToastProps {
  showSupportAction?: boolean
  supportSubject?: string
}

export type WarningToastProps = Omit<AlertToastProps, "variant">

const useToast = () => {
  const tmp = useShadToast()
  const toast = tmp.toast as (
    props: ToastPayload
  ) => ReturnType<typeof tmp.toast>
  const { t } = useTranslation()

  const alertToast = useCallback(
    ({
      showSupportAction = false,
      supportSubject,
      title,
      description,
      action,
      ...props
    }: AlertToastProps) => {
      const withIcon = (content: ReactNode) => (
        <div className="flex items-start">
          <CircleAlert className="h-6 w-6 shrink-0 text-yellow-500" />
          <div className="ml-2">{content}</div>
        </div>
      )

      return toast({
        duration: DEFAULT_DURATION,
        ...props,
        action:
          action ||
          (showSupportAction ? (
            <ToastAction
              altText={t("common.support.contactSupport")}
              onClick={() => {
                window.location.href = getSupportMailto(supportSubject)
              }}
            >
              {t("common.support.contactSupport")}
            </ToastAction>
          ) : undefined),
        // The icon leads the description row; when a toast only has a
        // title, lead the title row instead so the icon never sits alone.
        ...(description
          ? { title, description: withIcon(description) }
          : { title: withIcon(title) }),
      })
    },
    [toast, t]
  )

  const successToast = useCallback(
    ({ title, description, ...props }: ToastProps) => {
      const withIcon = (content: ReactNode) => (
        <>
          <Check className="h-6 w-6 inline-block text-green-500" />
          <span className="pl-2">{content}</span>
        </>
      )

      return toast({
        duration: DEFAULT_DURATION,
        ...props,
        ...(description
          ? { title, description: withIcon(description) }
          : { title: withIcon(title) }),
      })
    },
    [toast]
  )

  const warningToast = useCallback(
    ({ className, ...props }: WarningToastProps) =>
      alertToast({
        ...props,
        className: cn(validationErrorToastClassName, className),
      }),
    [alertToast]
  )

  return {
    ...tmp,
    alertToast: alertToast,
    successToast: successToast,
    warningToast,
  }
}

export default useToast
